/**
 * Task Pool Task Item Component
 * Single task row in the Task Pool modal with "Add to board" button
 */

import { memo, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import type { GlobalTask, TaskCategory } from '../../../types';
import { highlightMatches } from '../../utils/fuzzySearch';

interface TaskPoolTaskItemProps {
  task: GlobalTask;
  categories: TaskCategory[];
  searchQuery: string;
  onAddToBoard: (taskId: string) => void;
}

export const TaskPoolTaskItem = memo(function TaskPoolTaskItem({
  task,
  categories,
  searchQuery,
  onAddToBoard,
}: TaskPoolTaskItemProps) {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  const titleSegments = useMemo(
    () => highlightMatches(searchQuery, task.title),
    [searchQuery, task.title]
  );

  const taskCategories = useMemo(
    () => (task.categoryIds ?? []).map(id => categories.find(c => c.id === id)).filter(Boolean) as TaskCategory[],
    [task.categoryIds, categories]
  );

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: '4px 12px',
        borderBottom: '1px solid #f3f4f6',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        backgroundColor: isHovered ? '#f9fafb' : 'transparent',
        transition: 'background-color 0.1s',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 400,
            color: '#1f2937',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {titleSegments.map((segment, index) => (
            <span
              key={index}
              style={{
                backgroundColor: segment.highlight ? '#fef08a' : 'transparent',
                borderRadius: segment.highlight ? 2 : 0,
                padding: segment.highlight ? '0 1px' : 0,
              }}
            >
              {segment.text}
            </span>
          ))}
        </div>
        {taskCategories.length > 0 && (
          <div style={{ display: 'flex', gap: 3, marginTop: 2, flexWrap: 'wrap' }}>
            {taskCategories.map((cat) => (
              <span
                key={cat.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 2,
                  fontSize: 9,
                  padding: '0px 4px',
                  borderRadius: 9999,
                  backgroundColor: cat.color + '20',
                  color: cat.color,
                  border: `1px solid ${cat.color}40`,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: cat.color,
                  }}
                />
                {cat.name}
              </span>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onAddToBoard(task.id);
        }}
        title={t('boardv3.taskPool.addToBoard')}
        style={{
          padding: '2px 6px',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          fontSize: 10,
          fontWeight: 400,
          color: '#3b82f6',
          backgroundColor: isHovered ? '#eff6ff' : 'transparent',
          border: '1px solid',
          borderColor: isHovered ? '#93c5fd' : 'transparent',
          borderRadius: 4,
          cursor: 'pointer',
          transition: 'all 0.1s',
          flexShrink: 0,
        }}
      >
        <Plus size={11} />
        <span>{t('boardv3.taskPool.add')}</span>
      </button>
    </div>
  );
});
